// src/componentes/tienda/tiendaPublica/DetalleProducto.js
// Detalle rápido del producto (Radix Dialog): foto grande, descripción
// completa, color en muestras y talla en botones con su disponibilidad, cantidad y
// «Agregar al carrito». Se abre desde la tarjeta y con el enlace #p-{id}.
import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, Heart, Link2, Minus, Plus, ShoppingCart, X } from 'lucide-react';
import {
  categoriaDe, galeriaProducto, marcaDe, ETIQUETA_NIVEL_STOCK, nivelStock, soles, stockPublico, unidadesAgregables,
} from '../../../dominio/logicaTiendaPublica';
import { FotoProducto, MarcoFoto, useSeleccionProducto } from './TarjetaProducto';
import { SelectorColor } from './SelectorColor';
import { nombreColor } from '../../../dominio/coloresProducto';
import { enlaceCompartirTienda } from '../../../servicios/catalogoJsonServicio';

// Tallas como botones: sin stock no se eligen; con stock en otro color se
// eligen igual y sueltan el color que no combina.
const OpcionesTalla = ({ opciones, disponibles, conStock, valor, onElegir }) => (
  <fieldset className="tp-detalle-opciones">
    <legend>Talla{valor ? <span>: {valor}</span> : null}</legend>
    <div>
      {opciones.map((opcion) => {
        const hay = conStock.includes(opcion), combina = disponibles.includes(opcion);
        return <button type="button" key={opcion} className={`tp-chip-opcion${hay && !combina ? ' is-sin-combinacion' : ''}`}
          aria-pressed={valor === opcion} disabled={!hay} onClick={() => onElegir(valor === opcion ? '' : opcion)}
          aria-label={`Talla ${opcion}${!hay ? ', sin stock' : !combina ? ', no disponible en el color elegido' : ''}`}>{opcion}</button>;
      })}
    </div>
  </fieldset>
);

// Galería: foto grande y, si hay más de una, miniaturas para cambiarla. Elegir
// otro color vuelve a la primera (la de ese color).
const GaleriaDetalle = ({ producto, color }) => {
  const fotos = galeriaProducto(producto, color);
  const [actual, setActual] = useState(0);
  useEffect(() => { setActual(0); }, [color]);
  const indice = Math.min(actual, Math.max(0, fotos.length - 1));
  if (fotos.length < 2) return <div className="tp-detalle-foto"><FotoProducto producto={producto} tamano="detalle" color={color} /></div>;
  return <div className="tp-detalle-galeria">
    <div className="tp-detalle-foto">
      <MarcoFoto key={fotos[indice]} url={fotos[indice]} alt={`${producto.nombre}, foto ${indice + 1} de ${fotos.length}`} eager />
    </div>
    <div className="tp-detalle-miniaturas" role="group" aria-label={`Fotos de ${producto.nombre}`}>
      {fotos.map((url, i) => <button type="button" key={url} aria-pressed={i === indice} aria-label={`Ver foto ${i + 1} de ${fotos.length}`}
        onClick={() => setActual(i)}><img src={url} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" /></button>)}
    </div>
  </div>;
};

export const DetalleProducto = ({ producto, enCarrito = 0, favorito = false, onFavorito, onAgregar, onCerrar }) => {
  const sel = useSeleccionProducto(producto);
  const [cantidad, setCantidad] = useState(1);
  const [copiado, setCopiado] = useState(false);
  const nivel = nivelStock(stockPublico(producto));
  const maximo = Math.max(1, unidadesAgregables({ stock: sel.stock, enCarrito: 0 }));
  useEffect(() => { setCantidad((actual) => Math.min(actual, maximo)); }, [maximo]);
  const agotado = nivel === 'agotado';
  const puedeAgregar = !agotado && !sel.faltaVariante && sel.stock > 0;

  const compartir = async () => {
    const url = enlaceCompartirTienda(producto.gymId || window.location.pathname.split('/')[2] || '', producto.id);
    try { await navigator.clipboard.writeText(url); setCopiado(true); setTimeout(() => setCopiado(false), 2500); }
    catch { window.prompt('Copia el enlace del producto', url); }
  };

  return <Dialog.Root open onOpenChange={(abierto) => { if (!abierto) onCerrar(); }}>
    <Dialog.Portal>
      <Dialog.Overlay className="tp-velo" />
      <Dialog.Content className="tp-detalle" aria-describedby={producto.descripcion ? 'tp-detalle-descripcion' : undefined}>
        <Dialog.Close className="tp-detalle-cerrar" aria-label="Cerrar detalle"><X size={20} aria-hidden="true" /></Dialog.Close>
        <GaleriaDetalle producto={producto} color={sel.color} />
        <div className="tp-detalle-info">
          <span className="tp-tarjeta-categoria">{categoriaDe(producto)}{marcaDe(producto) ? ` · ${marcaDe(producto)}` : ''}</span>
          <Dialog.Title className="tp-detalle-titulo">{producto.nombre}</Dialog.Title>
          {producto.sku && <span className="tp-detalle-sku">Código {sel.varianteElegida?.sku || producto.sku}</span>}
          <p className="tp-precio tp-precio--grande">{soles(producto.precio)}</p>
          <p className={`tp-detalle-stock tp-detalle-stock--${nivel}`}>{ETIQUETA_NIVEL_STOCK[nivel]}</p>
          {producto.descripcion && <p id="tp-detalle-descripcion" className="tp-detalle-descripcion">{producto.descripcion}</p>}

          {producto.colores?.length > 0 && <fieldset className="tp-detalle-opciones">
            <legend>Color{sel.color ? <span>: {nombreColor(sel.color)}</span> : null}</legend>
            <SelectorColor colores={producto.colores} tonos={producto.tonosColor} disponibles={sel.opciones.colores} conStock={sel.opciones.coloresConStock}
              valor={sel.color} onElegir={sel.elegirColor} deshabilitado={agotado} />
          </fieldset>}
          {producto.tallas?.length > 0 && <OpcionesTalla opciones={producto.tallas} disponibles={sel.opciones.tallas}
            conStock={sel.opciones.tallasConStock} valor={sel.talla} onElegir={sel.elegirTalla} />}

          <div className="tp-detalle-compra">
            <div className="tp-cantidad" role="group" aria-label="Cantidad">
              <button type="button" aria-label="Restar una unidad" disabled={!puedeAgregar || cantidad <= 1}
                onClick={() => setCantidad((n) => Math.max(1, n - 1))}><Minus size={16} aria-hidden="true" /></button>
              <output aria-live="polite">{cantidad}</output>
              <button type="button" aria-label="Sumar una unidad" disabled={!puedeAgregar || cantidad >= maximo}
                onClick={() => setCantidad((n) => Math.min(maximo, n + 1))}><Plus size={16} aria-hidden="true" /></button>
            </div>
            <button type="button" className="tp-agregar tp-agregar--grande" disabled={!puedeAgregar}
              onClick={() => { onAgregar(producto, cantidad, sel.seleccion); onCerrar(); }}>
              <ShoppingCart size={18} aria-hidden="true" />
              <span>{agotado ? 'Agotado' : sel.faltaVariante ? 'Elige una opción' : 'Agregar al carrito'}</span>
            </button>
          </div>
          {enCarrito > 0 && <span className="tp-en-carrito"><Check size={14} aria-hidden="true" /> {enCarrito} en tu carrito</span>}

          <div className="tp-detalle-acciones">
            <button type="button" className="tp-boton-texto" aria-pressed={favorito} onClick={() => onFavorito(producto.id)}>
              <Heart size={16} aria-hidden="true" /> {favorito ? 'En favoritos' : 'Guardar en favoritos'}
            </button>
            <button type="button" className="tp-boton-texto" onClick={compartir}>
              <Link2 size={16} aria-hidden="true" /> {copiado ? 'Enlace copiado' : 'Copiar enlace del producto'}
            </button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
};
